import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Loader2, Play, History, BookOpen, ArrowLeftRight, Zap, Clock, CheckCircle2,
  XCircle, AlertTriangle, ChevronDown, ChevronRight, Search, Package, FileOutput
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import PWMPriorityWizard from './PWMPriorityWizard';

const STATUS_COLORS = {
  UNALLOCATED: { bg: '#ef444420', text: '#ef4444' },
  PARTIAL_HARD: { bg: '#f59e0b20', text: '#f59e0b' },
  FULLY_ALLOCATED: { bg: '#22c55e20', text: '#22c55e' },
};

const RUN_STATUS_COLORS = {
  RUNNING: '#f59e0b', COMPLETED: '#22c55e', FAILED: '#ef4444', COMPLETED_WITH_WARNINGS: '#f59e0b',
};

export default function PWMAllocationEngine({ projectId }) {
  const [tab, setTab] = useState('engine');
  const [running, setRunning] = useState(null);
  const [stateDist, setStateDist] = useState({});
  const [runs, setRuns] = useState([]);
  const [ledger, setLedger] = useState({ entries: [], total: 0 });
  const [ledgerFilter, setLedgerFilter] = useState({ allocationType: '', lineId: '', spoolNumber: '' });
  const [expandedRun, setExpandedRun] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [dealloc, setDealloc] = useState({ spoolNumber: '', sapCode: '', quantity: '', reason: '', targetSpool: '' });
  const [lastRun, setLastRun] = useState(null); // { type, label, linesUpdated, spoolsUpdated, qtyAllocated, warnings }
  const [deallocating, setDeallocating] = useState(false);
  const [inventory, setInventory] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [invChartFilter, setInvChartFilter] = useState(null);
  const [pendingIssues, setPendingIssues] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [issuingSpool, setIssuingSpool] = useState(null);
  const [issuingLine, setIssuingLine] = useState(null);
  const [expandedIssueLine, setExpandedIssueLine] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [distRes, runsRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/pwm/allocation/state-distribution`),
        api.get(`/v1/projects/${projectId}/pwm/allocation/runs?limit=20`),
      ]);
      setStateDist(distRes.data || {});
      setRuns(runsRes.data || []);
    } catch { /* ignore */ }
  }, [projectId]);

  const loadLedger = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (ledgerFilter.allocationType) params.set('allocationType', ledgerFilter.allocationType);
      if (ledgerFilter.lineId) params.set('lineId', ledgerFilter.lineId);
      if (ledgerFilter.spoolNumber) params.set('spoolNumber', ledgerFilter.spoolNumber);
      const res = await api.get(`/v1/projects/${projectId}/pwm/allocation/v2/ledger?${params}`);
      setLedger(res.data || { entries: [], total: 0 });
    } catch { /* ignore */ }
  }, [projectId, ledgerFilter]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (tab === 'ledger') loadLedger(); }, [tab, loadLedger]);

  const loadInventory = useCallback(async () => {
    setInventoryLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/pwm/allocation/inventory-summary`);
      setInventory(res.data);
    } catch { /* ignore */ }
    setInventoryLoading(false);
  }, [projectId]);

  useEffect(() => { if (tab === 'inventory') loadInventory(); }, [tab, loadInventory]);

  const loadPendingIssues = useCallback(async () => {
    setPendingLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/pwm/material-issue/pending`);
      setPendingIssues(res.data);
    } catch { setPendingIssues(null); }
    setPendingLoading(false);
  }, [projectId]);

  useEffect(() => { if (tab === 'issue') loadPendingIssues(); }, [tab, loadPendingIssues]);

  const issueSingleSpool = async (spoolNumber) => {
    setIssuingSpool(spoolNumber);
    try {
      const res = await api.post(`/v1/projects/${projectId}/pwm/material-issue/spool/${spoolNumber}`);
      toast.success(`Issued ${spoolNumber} — MIR: ${res.data.mirNumber}`);
      loadPendingIssues();
      loadData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || 'Issue failed');
    }
    setIssuingSpool(null);
  };

  const issueEntireLine = async (lineId) => {
    setIssuingLine(lineId);
    try {
      const res = await api.post(`/v1/projects/${projectId}/pwm/material-issue/line/${lineId}`);
      toast.success(`Issued ${res.data.issued} of ${res.data.total} spools for ${lineId}`);
      loadPendingIssues();
      loadData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || 'Bulk issue failed');
    }
    setIssuingLine(null);
  };

  const runAllocation = async (type) => {
    setRunning(type);
    try {
      const endpoint = type === 'soft' ? 'run-soft' : (type === 'hard' ? 'run-hard' : 'run-full');
      const res = await api.post(`/v1/projects/${projectId}/pwm/allocation/v2/${endpoint}`);
      const label = type === 'soft' ? 'Soft allocation' : type === 'hard' ? 'Hard allocation' : 'Full allocation';
      // Surface the analysis: lines/spools affected, qty allocated, warnings
      setLastRun({ type, label, ...(res.data || {}) });
      const affected = res.data?.linesUpdated ?? res.data?.spoolsUpdated ?? res.data?.updated ?? res.data?.count ?? 0;
      const qty = res.data?.qtyAllocated ?? res.data?.totalAllocated ?? res.data?.inchDiaAllocated ?? 0;
      toast.success(`${label} completed · ${affected} record(s) updated${qty ? ` · ${qty} qty allocated` : ''}`);
      loadData();
      if (tab === 'inventory') loadInventory();
    } catch (e) { toast.error(e?.response?.data?.detail || `Allocation failed`); }
    setRunning(null);
  };

  const handleDealloc = async () => {
    if (!dealloc.spoolNumber || !dealloc.reason) return;
    setDeallocating(true);
    try {
      const body = {
        spoolNumber: dealloc.spoolNumber, reason: dealloc.reason,
        ...(dealloc.sapCode && { sapCode: dealloc.sapCode }),
        ...(dealloc.quantity && { quantity: parseFloat(dealloc.quantity) }),
        ...(dealloc.targetSpool && { targetSpoolNumber: dealloc.targetSpool }),
      };
      const res = await api.post(`/v1/projects/${projectId}/pwm/allocation/v2/de-allocate`, body);
      toast.success(`De-allocated from ${dealloc.spoolNumber}. New alloc: ${res.data.newAllocatedPercent}%`);
      setDealloc({ spoolNumber: '', sapCode: '', quantity: '', reason: '', targetSpool: '' });
      loadData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || 'De-allocation failed');
    }
    setDeallocating(false);
  };

  const allStates = ['UNALLOCATED', 'PARTIAL_HARD', 'FULLY_ALLOCATED'];
  const totalLines = Object.values(stateDist).reduce((s, v) => s + (v?.count || 0), 0);

  const tabs = [
    { id: 'engine', label: 'Run Engine', icon: Zap },
    { id: 'issue', label: 'Material Issue', icon: FileOutput },
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'wizard', label: 'Priority Wizard', icon: Play },
    { id: 'history', label: 'Run History', icon: History },
    { id: 'ledger', label: 'Allocation Ledger', icon: BookOpen },
    { id: 'dealloc', label: 'De-allocation', icon: ArrowLeftRight },
  ];

  if (showWizard) {
    return <PWMPriorityWizard projectId={projectId} onClose={() => { setShowWizard(false); loadData(); }} />;
  }

  return (
    <div className="space-y-3" data-testid="pwm-allocation-engine-v2">
      {/* Sub-tabs */}
      <div className="flex gap-1 flex-wrap">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => t.id === 'wizard' ? setShowWizard(true) : setTab(t.id)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-medium transition-colors"
              style={{ background: tab === t.id ? '#143A6B' : 'transparent', color: tab === t.id ? '#fff' : 'var(--text-muted)' }}
              data-testid={`alloc-tab-${t.id}`}>
              <Icon className="w-3 h-3" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ═══ ENGINE TAB ═══ */}
      {tab === 'engine' && (
        <div className="space-y-3">
          {/* State Distribution */}
          <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <p className="text-[10px] font-medium mb-2" style={{ color: 'var(--text-muted)' }}>State Distribution ({totalLines} lines)</p>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {allStates.map(st => {
                const c = stateDist[st] || { count: 0 };
                const sc = STATUS_COLORS[st] || {};
                return (
                  <div key={st} className="p-2 rounded-lg text-center" style={{ background: sc.bg }}>
                    <p className="text-lg font-bold" style={{ color: sc.text }}>{c.count}</p>
                    <p className="text-[8px] font-medium" style={{ color: sc.text }}>{st.replace(/_/g, ' ')}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Run Controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { type: 'soft', label: 'Run Soft Allocation', desc: 'Line-level allocation against PO ordered qty (OrderedQty − HardAllocatedQty)', color: '#3b82f6' },
              { type: 'hard', label: 'Run Hard Allocation', desc: 'Spool-level allocation against site inventory (ReceivedQty − IssuedQty)', color: '#22c55e' },
              { type: 'full', label: 'Run Full Allocation', desc: 'Soft + Hard in one pass with full audit trail and status rollup', color: '#143A6B' },
            ].map(r => (
              <div key={r.type} className="p-3 rounded-lg border" style={{ borderColor: r.color + '40', background: r.color + '08' }}>
                <p className="text-xs font-bold mb-0.5" style={{ color: r.color }}>{r.label}</p>
                <p className="text-[9px] mb-2" style={{ color: 'var(--text-muted)' }}>{r.desc}</p>
                <Button size="sm" onClick={() => runAllocation(r.type)} disabled={running != null}
                  className="text-[10px] w-full" style={{ background: r.color, color: '#fff' }}
                  data-testid={`v2-run-${r.type}-btn`}>
                  {running === r.type ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                  {running === r.type ? 'Running...' : 'Execute'}
                </Button>
              </div>
            ))}
          </div>

          {/* Allocation Analysis — appears after any Execute button completes */}
          {lastRun && (
            <div className="mt-3 p-3 rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }} data-testid="pwm-last-run-analysis">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>Last Run · {lastRun.label}</p>
                <button onClick={() => setLastRun(null)} className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Dismiss</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
                {[
                  ['Lines Updated',   lastRun.linesUpdated],
                  ['Spools Updated',  lastRun.spoolsUpdated],
                  ['Qty Allocated',   lastRun.qtyAllocated ?? lastRun.totalAllocated ?? lastRun.inchDiaAllocated],
                  ['Fully Allocated', lastRun.fullyAllocated],
                  ['Partial',         lastRun.partial],
                  ['Warnings',        Array.isArray(lastRun.warnings) ? lastRun.warnings.length : lastRun.warnings],
                  ['Run ID',          lastRun.runId ?? lastRun.id],
                  ['Duration (ms)',   lastRun.durationMs],
                ].filter(([_, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => (
                  <div key={k} className="p-2 rounded" style={{ background: 'var(--bg-input)' }}>
                    <p style={{ color: 'var(--text-muted)' }}>{k}</p>
                    <p className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{typeof v === 'number' ? v.toLocaleString() : String(v)}</p>
                  </div>
                ))}
              </div>
              {Array.isArray(lastRun.warnings) && lastRun.warnings.length > 0 && (
                <ul className="mt-2 text-[10px] space-y-0.5" style={{ color: '#f59e0b' }}>
                  {lastRun.warnings.slice(0, 5).map((w, i) => <li key={i}>• {typeof w === 'string' ? w : (w.message || JSON.stringify(w))}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ MATERIAL ISSUE TAB ═══ */}
      {tab === 'issue' && (
        <div className="space-y-3" data-testid="pwm-material-issue">
          {pendingLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
          ) : pendingIssues ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="p-3 rounded-lg border" style={{ borderColor: '#f59e0b30', background: '#f59e0b08' }}>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Pending Issue</p>
                  <p className="text-xl font-bold font-mono" style={{ color: '#f59e0b' }}>{pendingIssues.totalPending}</p>
                  <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>spools ready to issue</p>
                </div>
                <div className="p-3 rounded-lg border" style={{ borderColor: '#3b82f630', background: '#3b82f608' }}>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Lines with Pending</p>
                  <p className="text-xl font-bold font-mono" style={{ color: '#3b82f6' }}>{pendingIssues.lineCount}</p>
                  <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>piping lines</p>
                </div>
                <div className="p-3 rounded-lg border" style={{ borderColor: '#22c55e30', background: '#22c55e08' }}>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Status</p>
                  <p className="text-sm font-bold" style={{ color: pendingIssues.totalPending > 0 ? '#f59e0b' : '#22c55e' }}>
                    {pendingIssues.totalPending > 0 ? 'Action Required' : 'All Issued'}
                  </p>
                  <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>100% allocated, not yet issued</p>
                </div>
              </div>

              {pendingIssues.totalPending === 0 ? (
                <div className="p-6 rounded-lg border text-center" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: '#22c55e' }} />
                  <p className="text-sm font-bold" style={{ color: '#22c55e' }}>All Materials Issued</p>
                  <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>No spools pending material issue. Run allocation to generate new eligible spools.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(pendingIssues.lineGroups || {}).map(([lineId, group]) => {
                    const isExpanded = expandedIssueLine === lineId;
                    return (
                      <div key={lineId} className="rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }} data-testid={`issue-line-${lineId}`}>
                        <div className="flex items-center justify-between px-3 py-2">
                          <button className="flex items-center gap-2" onClick={() => setExpandedIssueLine(isExpanded ? null : lineId)}>
                            {isExpanded ? <ChevronDown className="w-3 h-3" style={{ color: '#143A6B' }} /> : <ChevronRight className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />}
                            <span className="text-[11px] font-mono font-bold" style={{ color: '#143A6B' }}>{lineId}</span>
                            <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: '#f59e0b20', color: '#f59e0b' }}>{group.count} spools</span>
                          </button>
                          <Button size="sm" onClick={() => issueEntireLine(lineId)} disabled={issuingLine === lineId} className="text-[10px] h-6"
                            style={{ background: '#22c55e' }} data-testid={`issue-all-line-${lineId}`}>
                            {issuingLine === lineId ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileOutput className="w-3 h-3 mr-1" />}
                            Issue All ({group.count})
                          </Button>
                        </div>
                        {isExpanded && (
                          <div style={{ borderTop: '1px solid var(--border-color)' }}>
                            <table className="w-full text-[10px]">
                              <thead>
                                <tr style={{ background: 'var(--card-bg-alt)' }}>
                                  <th className="px-3 py-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Spool #</th>
                                  <th className="px-3 py-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Fab Location</th>
                                  <th className="px-3 py-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Inch-Dia</th>
                                  <th className="px-3 py-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Alloc %</th>
                                  <th className="px-3 py-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.spools.map(s => (
                                  <tr key={s.spoolNumber} style={{ borderTop: '1px solid var(--border-color)' }} data-testid={`issue-row-${s.spoolNumber}`}>
                                    <td className="px-3 py-1.5 font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{s.spoolNumber}</td>
                                    <td className="px-3 py-1.5" style={{ color: 'var(--text-muted)' }}>{s.fabricationLocation || '-'}</td>
                                    <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{(s.spoolInchDia || 0).toFixed(1)}</td>
                                    <td className="px-3 py-1.5 text-right font-mono" style={{ color: '#22c55e' }}>{(s.overallAllocatedPercent || 0).toFixed(0)}%</td>
                                    <td className="px-3 py-1.5 text-center">
                                      <Button size="sm" onClick={() => issueSingleSpool(s.spoolNumber)} disabled={issuingSpool === s.spoolNumber}
                                        className="text-[9px] h-5 px-2" style={{ background: '#143A6B' }} data-testid={`issue-btn-${s.spoolNumber}`}>
                                        {issuingSpool === s.spoolNumber ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : 'Issue'}
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>No data available. Run allocation first.</p>
          )}
        </div>
      )}

      {/* ═══ INVENTORY TAB ═══ */}
      {tab === 'inventory' && (
        <div className="space-y-3" data-testid="inventory-summary">
          {inventoryLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
          ) : inventory ? (
            <>
              {/* MTM sync banner — surfaces how many SAP codes are getting their
                  live counters from the MTM module (post-GRN intake). */}
              {(inventory.mtmSyncedSapCount ?? 0) > 0 && (
                <div className="px-3 py-1.5 rounded-md text-[10px] flex items-center justify-between"
                  style={{ background: '#10b98115', border: '1px solid #10b98140', color: '#10b981' }}
                  data-testid="pwm-inv-mtm-banner">
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
                    <strong>{inventory.mtmSyncedSapCount}</strong> of {inventory.sapCodeCount} SAP codes synced live from <strong>Material Tracking (MTM)</strong>
                  </span>
                  <span style={{ color: 'var(--text-muted)' }}>
                    Reserved <strong>{(inventory.totals.mtmReserved || 0).toLocaleString()}</strong> · Awaiting Insp. <strong>{(inventory.totals.mtmAwaitingInspection || 0).toLocaleString()}</strong>
                  </span>
                </div>
              )}
              {/* Pipeline Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: 'Line BoQ Required', value: inventory.totals.lineBoqReq, color: '#143A6B', sub: `${inventory.sapCodeCount} SAP codes` },
                  { label: 'PO Qty (Ordered)', value: inventory.totals.poQty, color: '#3b82f6', sub: `In-Transit: ${inventory.totals.inTransit?.toLocaleString()}` },
                  { label: 'GRN Qty (Received)', value: inventory.totals.grnQty, color: '#f59e0b', sub: `QA Accepted: ${inventory.totals.qaAccepted?.toLocaleString()}` },
                  { label: 'Available After Alloc', value: inventory.totals.availableAfterAlloc, color: '#22c55e', sub: `Hard Allocated: ${inventory.totals.hardAllocated?.toLocaleString()}` },
                  { label: 'Issued Qty', value: inventory.totals.issuedQty, color: '#a855f7', sub: `From inventory to spools` },
                  { label: 'Balance After Issue', value: inventory.totals.balanceAfterIssue, color: '#06b6d4', sub: `QA Accepted - Issued` },
                ].map(c => (
                  <div key={c.label} className="p-3 rounded-lg border" style={{ borderColor: c.color + '30', background: c.color + '08' }}>
                    <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>{c.label}</p>
                    <p className="text-xl font-bold font-mono" style={{ color: c.color }}>{c.value?.toLocaleString()}</p>
                    <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{c.sub}</p>
                  </div>
                ))}
              </div>

              {/* Per-SAP Code Interactive Chart */}
              <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Inventory by SAP Code</p>
                  {invChartFilter && (
                    <button onClick={() => setInvChartFilter(null)} className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: '#143A6B20', color: '#143A6B' }}>
                      Showing: {invChartFilter} — Click to clear
                    </button>
                  )}
                </div>
                <div className="h-[200px]" data-testid="pwm-inv-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={(inventory.items || []).slice(0, 20)} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                      onClick={(data) => {
                        if (data?.activeLabel) setInvChartFilter(prev => prev === data.activeLabel ? null : data.activeLabel);
                      }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis dataKey="sapCode" tick={{ fill: 'var(--text-muted)', fontSize: 8 }} angle={-35} textAnchor="end" height={50} />
                      <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 9 }} />
                      <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 10 }}
                        formatter={(val, name) => [val?.toLocaleString(), name]} />
                      <Legend wrapperStyle={{ fontSize: 9 }} />
                      <Bar dataKey="lineBoqReq" name="Line BoQ" fill="#143A6B" />
                      <Bar dataKey="hardAllocated" name="Hard Alloc" fill="#22c55e" />
                      <Bar dataKey="issuedQty" name="Issued Qty" fill="#a855f7" />
                      <Bar dataKey="balanceAfterIssue" name="Balance After Issue" fill="#06b6d4" />
                      <Bar dataKey="availableAfterAlloc" name="Available" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Inventory Pipeline Flow Bar */}
              <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                <p className="text-[10px] font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Inventory Pipeline Flow</p>
                <div className="flex gap-0.5 h-6 rounded-lg overflow-hidden">
                  {[
                    { label: 'Hard Alloc', val: inventory.totals.hardAllocated, color: '#22c55e' },
                    { label: 'Issued', val: inventory.totals.issuedQty, color: '#a855f7' },
                    { label: 'Balance', val: inventory.totals.balanceAfterIssue, color: '#06b6d4' },
                    { label: 'Available', val: inventory.totals.availableAfterAlloc, color: '#10b981' },
                    { label: 'In Transit', val: inventory.totals.inTransit, color: '#f59e0b' },
                    { label: 'Not Ordered', val: Math.max(inventory.totals.lineBoqReq - inventory.totals.poQty, 0), color: '#ef4444' },
                  ].filter(s => s.val > 0).map(s => {
                    const total = Math.max(inventory.totals.lineBoqReq, 1);
                    const pct = (s.val / total) * 100;
                    return (
                      <div key={s.label} title={`${s.label}: ${s.val.toLocaleString()} (${pct.toFixed(1)}%)`}
                        className="flex items-center justify-center text-[7px] font-bold text-white cursor-help transition-all hover:opacity-80"
                        style={{ background: s.color, width: `${Math.max(pct, 2)}%` }}>
                        {pct > 8 ? s.label : ''}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-3 mt-2">
                  {[
                    { label: 'Hard Allocated', color: '#22c55e' },
                    { label: 'Issued', color: '#a855f7' },
                    { label: 'Balance After Issue', color: '#06b6d4' },
                    { label: 'Available at Site', color: '#10b981' },
                    { label: 'In Transit', color: '#f59e0b' },
                    { label: 'Not Ordered', color: '#ef4444' },
                  ].map(l => (
                    <span key={l.label} className="flex items-center gap-1 text-[8px]" style={{ color: 'var(--text-muted)' }}>
                      <span className="w-2 h-2 rounded-sm" style={{ background: l.color }} /> {l.label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Per-SAP Code Pipeline Table (filtered by chart click) */}
              <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
                <table className="w-full text-[10px]" data-testid="inventory-table">
                  <thead>
                    <tr style={{ background: 'var(--card-bg-alt)' }}>
                      <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>SAP Code</th>
                      <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Description</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#143A6B' }}>Line BoQ</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#a855f7' }}>Spool BoQ</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#3b82f6', borderLeft: '2px solid var(--border-color)' }}>PO Qty</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#f59e0b' }}>In-Transit</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#eab308', borderLeft: '2px solid var(--border-color)' }}>GRN Qty</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#84cc16' }}>QA Accepted</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#22c55e', borderLeft: '2px solid var(--border-color)' }}>Hard Alloc</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#10b981' }}>Avail After Alloc</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#a855f7', borderLeft: '2px solid var(--border-color)' }}>Issued Qty</th>
                      <th className="p-2 text-right font-medium" style={{ color: '#06b6d4' }}>Bal After Issue</th>
                      <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>POs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invChartFilter ? inventory.items.filter(i => i.sapCode === invChartFilter) : inventory.items).map(item => (
                      <tr key={item.sapCode} style={{ borderTop: '1px solid var(--border-color)', background: invChartFilter === item.sapCode ? '#143A6B10' : 'var(--card-bg)' }}
                        data-testid={`inv-row-${item.sapCode}`}>
                        <td className="p-2 font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>
                          {item.sapCode}
                          {item.mtmLive && (
                            <span title={`Live MTM: ${item.mtmMaterialCount} material doc(s) · Reserved ${(item.mtmReserved||0).toLocaleString()} · Awaiting Insp. ${(item.mtmAwaitingInspection||0).toLocaleString()}`}
                              className="ml-1.5 px-1 py-0 rounded text-[7px] font-bold inline-flex items-center gap-0.5"
                              style={{ background: '#10b98120', color: '#10b981', border: '1px solid #10b98140' }}
                              data-testid={`mtm-live-${item.sapCode}`}>
                              <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"/> MTM
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-[9px]" style={{ color: 'var(--text-muted)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#143A6B' }}>{item.lineBoqReq?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#a855f7' }}>{item.spoolBoqReq?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#3b82f6', borderLeft: '2px solid var(--border-color)' }}>{item.poQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#f59e0b' }}>{item.inTransit?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#eab308', borderLeft: '2px solid var(--border-color)' }}>{item.grnQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#84cc16' }}>{item.qaAccepted?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#22c55e', borderLeft: '2px solid var(--border-color)' }}>{item.hardAllocated?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: item.availableAfterAlloc > 0 ? '#10b981' : '#ef4444' }}>{item.availableAfterAlloc?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono" style={{ color: '#a855f7', borderLeft: '2px solid var(--border-color)' }}>{item.issuedQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: item.balanceAfterIssue > 0 ? '#06b6d4' : '#ef4444' }}>{item.balanceAfterIssue?.toLocaleString()}</td>
                        <td className="p-2 text-center" style={{ color: 'var(--text-primary)' }}>{item.poCount}</td>
                      </tr>
                    ))}
                  </tbody>
                  {!invChartFilter && (
                    <tfoot>
                      <tr style={{ borderTop: '2px solid var(--border-color)', background: 'var(--card-bg-alt)' }}>
                        <td className="p-2 font-bold" style={{ color: 'var(--text-primary)' }} colSpan={2}>TOTAL ({inventory.sapCodeCount} SAP codes)</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#143A6B' }}>{inventory.totals.lineBoqReq?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#a855f7' }}>{inventory.totals.spoolBoqReq?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#3b82f6', borderLeft: '2px solid var(--border-color)' }}>{inventory.totals.poQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#f59e0b' }}>{inventory.totals.inTransit?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#eab308', borderLeft: '2px solid var(--border-color)' }}>{inventory.totals.grnQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#84cc16' }}>{inventory.totals.qaAccepted?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#22c55e', borderLeft: '2px solid var(--border-color)' }}>{inventory.totals.hardAllocated?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#10b981' }}>{inventory.totals.availableAfterAlloc?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#a855f7', borderLeft: '2px solid var(--border-color)' }}>{inventory.totals.issuedQty?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono font-bold" style={{ color: '#06b6d4' }}>{inventory.totals.balanceAfterIssue?.toLocaleString()}</td>
                        <td className="p-2 text-center font-bold" style={{ color: 'var(--text-primary)' }}>{inventory.totalPOs}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          ) : (
            <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>No inventory data available</p>
          )}
        </div>
      )}

      {/* ═══ RUN HISTORY TAB ═══ */}
      {tab === 'history' && (
        <div className="space-y-2" data-testid="run-history">
          {runs.length === 0 && <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>No allocation runs yet</p>}
          {runs.map(run => (
            <div key={run.runId} className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex items-center justify-between p-2.5 cursor-pointer" style={{ background: 'var(--card-bg)' }}
                onClick={() => setExpandedRun(expandedRun === run.runId ? null : run.runId)}>
                <div className="flex items-center gap-2">
                  {expandedRun === run.runId ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  <span className="font-mono text-[10px] font-bold" style={{ color: 'var(--accent-primary)' }}>{run.runId}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{
                    background: run.runType === 'SOFT' ? '#3b82f620' : run.runType === 'HARD' ? '#22c55e20' : '#143A6B20',
                    color: run.runType === 'SOFT' ? '#3b82f6' : run.runType === 'HARD' ? '#22c55e' : '#143A6B',
                  }}>{run.runType}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{run.startedAt?.slice(0, 19)}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{
                    background: RUN_STATUS_COLORS[run.runStatus] + '20',
                    color: RUN_STATUS_COLORS[run.runStatus],
                  }}>
                    {run.runStatus === 'COMPLETED' && <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" />}
                    {run.runStatus === 'FAILED' && <XCircle className="w-2.5 h-2.5 inline mr-0.5" />}
                    {run.runStatus}
                  </span>
                  {run.durationMs != null && <span className="text-[9px] font-mono" style={{ color: 'var(--text-muted)' }}>{run.durationMs}ms</span>}
                </div>
              </div>
              {expandedRun === run.runId && run.results && (
                <div className="px-3 pb-2.5 border-t" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-2 text-[9px]">
                    <div><p style={{ color: 'var(--text-muted)' }}>Lines</p><p className="font-bold" style={{ color: 'var(--text-primary)' }}>{run.results.linesProcessed}</p></div>
                    <div><p style={{ color: 'var(--text-muted)' }}>Spools</p><p className="font-bold" style={{ color: 'var(--text-primary)' }}>{run.results.spoolsProcessed ?? '—'}</p></div>
                    <div><p style={{ color: 'var(--text-muted)' }}>Allocations</p><p className="font-bold" style={{ color: '#22c55e' }}>{run.results.allocationsCreated}</p></div>
                    <div><p style={{ color: 'var(--text-muted)' }}>Promoted</p><p className="font-bold" style={{ color: '#3b82f6' }}>{run.results.linesPromoted ?? '—'}</p></div>
                    <div><p style={{ color: 'var(--text-muted)' }}>Demoted</p><p className="font-bold" style={{ color: '#f59e0b' }}>{run.results.linesDemoted ?? '—'}</p></div>
                    <div><p style={{ color: 'var(--text-muted)' }}>Errors</p><p className="font-bold" style={{ color: '#ef4444' }}>{run.results.errors?.length ?? 0}</p></div>
                  </div>
                  {/* Pre/Post snapshot comparison */}
                  {run.preRunSnapshot && run.postRunSnapshot && (
                    <div className="mt-2 p-2 rounded-lg" style={{ background: 'var(--card-bg)' }}>
                      <p className="text-[9px] font-bold mb-1" style={{ color: 'var(--text-muted)' }}>State Change</p>
                      <div className="flex gap-2 text-[9px]">
                        {allStates.map(st => {
                          const pre = run.preRunSnapshot.stateDistribution?.[st.toLowerCase().replace(/_([a-z])/g, (_, c) => c.toUpperCase())] ?? 0;
                          const post = run.postRunSnapshot.stateDistribution?.[st.toLowerCase().replace(/_([a-z])/g, (_, c) => c.toUpperCase())] ?? 0;
                          const diff = post - pre;
                          if (diff === 0) return null;
                          return (
                            <span key={st} style={{ color: diff > 0 ? '#22c55e' : '#ef4444' }}>
                              {st.replace(/_/g, ' ')}: {diff > 0 ? '+' : ''}{diff}
                            </span>
                          );
                        }).filter(Boolean)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ═══ LEDGER TAB ═══ */}
      {tab === 'ledger' && (
        <div className="space-y-2" data-testid="allocation-ledger">
          <div className="flex gap-2">
            <select value={ledgerFilter.allocationType} onChange={e => setLedgerFilter(p => ({ ...p, allocationType: e.target.value }))}
              className="text-[10px] px-2 py-1 rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <option value="">All Types</option><option value="SOFT">Soft</option><option value="HARD">Hard</option>
            </select>
            <input value={ledgerFilter.lineId} onChange={e => setLedgerFilter(p => ({ ...p, lineId: e.target.value }))}
              placeholder="Line ID" className="text-[10px] px-2 py-1 rounded border w-32"
              style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            <input value={ledgerFilter.spoolNumber} onChange={e => setLedgerFilter(p => ({ ...p, spoolNumber: e.target.value }))}
              placeholder="Spool No." className="text-[10px] px-2 py-1 rounded border w-32"
              style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            <Button size="sm" variant="outline" onClick={loadLedger} className="text-[10px]" data-testid="ledger-refresh">
              <Search className="w-3 h-3 mr-1" /> Search
            </Button>
          </div>
          <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>Total: {ledger.total} entries</p>

          <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)', maxHeight: 400 }}>
            <table className="w-full text-[9px]">
              <thead>
                <tr style={{ background: 'var(--card-bg-alt)', position: 'sticky', top: 0, zIndex: 2 }}>
                  <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Run ID</th>
                  <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Type</th>
                  <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Action</th>
                  <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Line ID</th>
                  <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Spool</th>
                  <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>SAP Code</th>
                  <th className="p-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Qty</th>
                  <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>PO</th>
                  <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Rank</th>
                  <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Time</th>
                </tr>
              </thead>
              <tbody>
                {ledger.entries.map((e, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td className="p-1.5 font-mono" style={{ color: 'var(--text-muted)' }}>{e.runId?.slice(0, 16)}</td>
                    <td className="p-1.5 text-center"><span className="px-1 py-0.5 rounded-full text-[8px] font-medium" style={{
                      background: e.allocationType === 'SOFT' ? '#3b82f620' : '#22c55e20',
                      color: e.allocationType === 'SOFT' ? '#3b82f6' : '#22c55e',
                    }}>{e.allocationType}</span></td>
                    <td className="p-1.5 text-center"><span className="text-[8px] font-medium" style={{
                      color: e.action === 'ALLOCATE' ? '#22c55e' : e.action === 'TRANSFER' ? '#3b82f6' : '#ef4444',
                    }}>{e.action}</span></td>
                    <td className="p-1.5 font-mono" style={{ color: 'var(--accent-primary)' }}>{e.lineId}</td>
                    <td className="p-1.5 font-mono text-[8px]" style={{ color: 'var(--text-muted)' }}>{e.spoolNumber || '—'}</td>
                    <td className="p-1.5 font-mono" style={{ color: 'var(--text-primary)' }}>{e.sapCode}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: e.quantity >= 0 ? '#22c55e' : '#ef4444' }}>{e.quantity >= 0 ? '+' : ''}{e.quantity}</td>
                    <td className="p-1.5 font-mono text-[8px]" style={{ color: 'var(--text-muted)' }}>{e.poNumber}</td>
                    <td className="p-1.5 text-center font-mono" style={{ color: 'var(--text-muted)' }}>{e.allocationSortRank || '—'}</td>
                    <td className="p-1.5 text-[8px]" style={{ color: 'var(--text-muted)' }}>{e.timestamp?.slice(11, 19)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ DE-ALLOCATION TAB ═══ */}
      {tab === 'dealloc' && (
        <div className="space-y-3" data-testid="dealloc-tab">
          <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: '#ef444440' }}>
            <h4 className="text-sm font-bold mb-1" style={{ color: '#ef4444' }}>Hard De-allocation</h4>
            <p className="text-[9px] mb-3" style={{ color: 'var(--text-muted)' }}>
              Remove hard allocation from a spool. Cannot de-allocate if fabrication has started. Requires reason.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[10px]">
              <div>
                <label className="block mb-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>Spool Number *</label>
                <input value={dealloc.spoolNumber} onChange={e => setDealloc(p => ({ ...p, spoolNumber: e.target.value }))}
                  placeholder="SPL-..." className="w-full px-2 py-1.5 rounded border"
                  style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label className="block mb-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>SAP Code (optional)</label>
                <input value={dealloc.sapCode} onChange={e => setDealloc(p => ({ ...p, sapCode: e.target.value }))}
                  placeholder="SAP-..." className="w-full px-2 py-1.5 rounded border"
                  style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label className="block mb-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>Quantity (optional)</label>
                <input value={dealloc.quantity} onChange={e => setDealloc(p => ({ ...p, quantity: e.target.value }))} type="number"
                  className="w-full px-2 py-1.5 rounded border"
                  style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label className="block mb-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>Transfer to Spool (optional)</label>
                <input value={dealloc.targetSpool} onChange={e => setDealloc(p => ({ ...p, targetSpool: e.target.value }))}
                  placeholder="SPL-..." className="w-full px-2 py-1.5 rounded border"
                  style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div className="md:col-span-2">
                <label className="block mb-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>Reason *</label>
                <input value={dealloc.reason} onChange={e => setDealloc(p => ({ ...p, reason: e.target.value }))}
                  placeholder="Reason for de-allocation (required)" className="w-full px-2 py-1.5 rounded border"
                  style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
            </div>
            <Button size="sm" onClick={handleDealloc} disabled={deallocating || !dealloc.spoolNumber || !dealloc.reason}
              className="mt-3 text-[10px]" style={{ background: '#ef4444', color: '#fff' }} data-testid="dealloc-btn">
              {deallocating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowLeftRight className="w-3.5 h-3.5 mr-1" />}
              Execute De-allocation
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
